import { useState } from "react";
import { Link } from "react-router-dom";
import { PhoneCall, CheckCircle2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/brands";

const inputCls =
  "w-full border-2 border-hair bg-paper px-4 py-3 font-body text-sm text-ink placeholder:text-muted/70 transition-colors focus:border-accent focus:outline-none";

export default function BrandRequestContact({ brand }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please add your name, email and phone");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/brands/${brand.slug}/contact-request`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <section className="border-t border-hair py-8" data-testid="brand-request-contact-done">
        <div className="border border-hair p-6 sm:p-8">
          <div className="flex items-center gap-2 text-accent">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-serif text-xl font-medium text-ink">Request received</span>
          </div>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
            Thanks — we&apos;ve saved your request to reach {brand.name}. If you need help right now,
            a specialist can jump on a live chat and help you sort it in minutes.
          </p>
          <Link
            to="/chat"
            data-testid="request-contact-chat-cta"
            className="mt-5 inline-flex items-center gap-2 bg-accent px-6 py-3 font-mono text-sm uppercase tracking-widest text-paper transition-opacity hover:opacity-90"
          >
            <MessageSquare className="h-4 w-4" /> Chat with a specialist now
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="border-t border-hair py-8" data-testid="brand-request-contact">
      <h2 className="flex items-center gap-2 font-serif text-2xl font-medium text-ink">
        <PhoneCall className="h-5 w-5 text-accent" /> Request contact with {brand.name}
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted">
        Leave your details and we&apos;ll help you get connected to {brand.name}&apos;s official
        support. Prefer to talk to someone now? You can also start a live chat with a specialist.
      </p>
      <form onSubmit={submit} className="mt-6 border border-hair p-5 sm:p-6" data-testid="request-contact-form">
        <div className="grid gap-3 sm:grid-cols-3">
          <input value={form.name} onChange={set("name")} placeholder="Your name" data-testid="request-form-name" className={inputCls} />
          <input value={form.email} onChange={set("email")} type="email" placeholder="Email" data-testid="request-form-email" className={inputCls} />
          <input value={form.phone} onChange={set("phone")} placeholder="Contact number" data-testid="request-form-phone" className={inputCls} />
        </div>
        <textarea value={form.message} onChange={set("message")} rows={2} placeholder="What do you need help with? (optional)" data-testid="request-form-message" className={`mt-3 ${inputCls}`} />
        <button type="submit" disabled={submitting} data-testid="request-form-submit" className="mt-4 inline-flex items-center gap-2 bg-accent px-6 py-3 font-mono text-sm uppercase tracking-widest text-paper transition-opacity hover:opacity-90 disabled:opacity-50">
          <PhoneCall className="h-4 w-4" /> {submitting ? "Sending…" : "Request contact"}
        </button>
      </form>
    </section>
  );
}
